if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true' || this.adding) return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // "Add to Cart" says "Adding…" until the add is through and can't be pressed twice
      this.adding = true;
      this.setBtnState('adding');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let added = false;

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        added = true;
        this.setBtnState('added');

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // Update the drawer as soon as the add is through (AST, Oct 15 2026: it used to wait 700ms for the tick
          // animation; the button now says "Added" instead, so the drawer opens with it)
          (async () => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              const openDrawer = !quickAddDrawer && theme.settings.afterAtc === 'drawer';
              try {
                await this.cartDrawer.renderContents(data, openDrawer);

                // The add went through, so a drawer that says "empty" was rendered from stale markup
                if (this.cartDrawer.querySelector('cart-items[data-empty="true"]')) {
                  throw new Error('Cart drawer shows an empty cart after a successful add');
                }
              } catch (renderError) {
                console.log(renderError); // eslint-disable-line
                // Fetch the drawer again so it shows the real cart, rather than leaving the old markup
                this.cartDrawer.refresh(true, openDrawer);
              }
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          })();
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        // The quantity the line held before this add: the line's quantity now, less what was added. Worked out
        // from the add's own reply, so the add no longer waits on a /cart.js request first (AST, Oct 15 2026).
        const addedQty = parseInt(formData.get('quantity'), 10) || 1;
        const oldQuantity = Math.max(0, (data.quantity || 0) - addedQty);

        // Check if product was already in the cart
        if (oldQuantity > 0) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity,
              newQuantity: data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line

        // A failed add that Shopify gave no message for (network error, rate limit, non-JSON reply)
        if (!added && this.errorMsg && this.errorMsg.hidden) {
          this.setErrorMsgState(theme.strings.cartError);
        }

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // "Added" shows for 1.2s when the item went in; a failed add gets its label back at once
        this.adding = false;
        if (added) {
          clearTimeout(this.btnTimer);
          this.btnTimer = setTimeout(() => this.setBtnState(null), 1200);
        } else {
          this.setBtnState(null);
        }
      }
    }

    /**
     * Shows the add's progress on the button (AST, Oct 15 2026, Erika: "the button goes all white after you click it
     * and its confusing"). The label swaps to "Adding…" while the request runs and to "Added" once it is through,
     * then the button's own label comes back. The button keeps its colours the whole time (the theme's is-loading
     * class hid the label), is aria-busy while adding, and the sticky bar's button says the same.
     * A button drawn with icons inside (product card quick add) keeps the theme's spinner instead.
     * @param {?string} state - 'adding', 'added' or null for the button's own label.
     */
    setBtnState(state) {
      const btns = [this.submitBtn];
      if (this.closest('.cc-main-product, .product-main')) {
        const sticky = document.querySelector('[data-sticky-atc-btn]');
        if (sticky) btns.push(sticky);
      }
      const words = { adding: 'Adding\u2026', added: 'Added' };
      btns.forEach((btn) => {
        if (!btn) return;
        if (btn.firstElementChild) {
          // Icon buttons: the theme's own loading look
          btn.classList.toggle('is-loading', state === 'adding');
          if (state === 'adding') btn.setAttribute('aria-disabled', 'true');
          else btn.removeAttribute('aria-disabled');
          return;
        }
        if (state) {
          if (!btn.hasAttribute('data-ast-state')) {
            btn.astLabel = btn.textContent;
            // Keep the width, so a short button doesn't jump when its words change
            btn.style.minWidth = `${btn.offsetWidth}px`;
          }
          btn.setAttribute('data-ast-state', state);
          btn.textContent = btn.dataset[state === 'adding' ? 'addingText' : 'addedText'] || words[state];
          btn.astShown = btn.textContent;
          if (state === 'adding') btn.setAttribute('aria-busy', 'true');
          else btn.removeAttribute('aria-busy');
        } else if (btn.hasAttribute('data-ast-state')) {
          btn.removeAttribute('data-ast-state');
          btn.removeAttribute('aria-busy');
          btn.style.minWidth = '';
          // If the variant picker wrote a new label meanwhile (another size, sold out), that one stays
          if (btn.textContent === btn.astShown) btn.textContent = btn.astLabel;
        }
      });
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
